import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { BackButton } from '../components/common';
import { Heading, Text, Caption } from '../components/common/Typography';
import { Card, StatsCard } from '../components/common/Card';
import { SeasonProvider, useSeason } from '../context/SeasonContext';
import { useClubLeaderboard } from '../hooks/useClubLeaderboard';
import LeaderboardRow from '../components/leaderboard/LeaderboardRow';
import LeaderboardScopeToggle from '../components/leaderboard/LeaderboardScopeToggle';
import { getScopeHelperText } from '../utils/leaderboardPlayerScope';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';

const pageShellClass = `min-h-screen bg-background ${APP_PAGE_BLEED_ROOT}`;
const pageGutterClass = `max-w-app-shell mx-auto ${APP_PAGE_GUTTER} pt-3 md:pt-6 pb-8`;

const FullLeaderboardPage = ({ currentUser }) => {
  const { clubId, gameType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { season: selectedSeason } = useSeason();
  const [club, setClub] = useState(null);
  const [seasonFromUrl, setSeasonFromUrl] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const seasonIdFromUrl = searchParams.get('season');

  useEffect(() => {
    if (seasonIdFromUrl) {
      const fetchSeason = async () => {
        try {
          const { data, error } = await supabase
            .from('seasons')
            .select('*')
            .eq('id', seasonIdFromUrl)
            .single();

          if (!error && data) {
            setSeasonFromUrl(data);
          }
        } catch (err) {
          console.error('Error fetching season from URL:', err);
        }
      };
      fetchSeason();
    } else {
      setSeasonFromUrl(null);
    }
  }, [seasonIdFromUrl]);

  const displaySeason = seasonFromUrl || selectedSeason;

  const gameTypeConfig = useMemo(() => {
    switch (gameType) {
      case 'levels-male':
        return {
          title: "Men's Doubles",
          colorClass: 'bg-info-light text-info',
        };
      case 'levels-female':
        return {
          title: "Women's Doubles",
          colorClass: 'bg-pink-100 text-pink-600',
        };
      case 'mixed':
        return {
          title: 'Mixed Doubles',
          colorClass: 'bg-primary/15 text-primary',
        };
      case 'combined':
        return {
          title: 'Combined',
          colorClass: 'bg-success-light text-success-dark',
        };
      default:
        return null;
    }
  }, [gameType]);

  useEffect(() => {
    if (!clubId) return;
    supabase
      .from('clubs')
      .select('id, name, location')
      .eq('id', clubId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error fetching club:', error);
        } else {
          setClub(data);
        }
      });
  }, [clubId]);

  const {
    leaderboard,
    kpis,
    snapshotMovement,
    loading,
    error,
    scope,
    setScope,
  } = useClubLeaderboard({
    clubId,
    seasonId: displaySeason?.id,
    gameType,
  });

  const handleBack = () => {
    const qs = new URLSearchParams({ tab: 'leaderboard' });
    if (displaySeason?.id) qs.set('season', displaySeason.id);
    if (scope === 'all') qs.set('scope', 'all');
    navigate(`/clubs/${clubId}?${qs.toString()}`);
  };

  if (!gameTypeConfig) {
    return (
      <div className={pageShellClass}>
        <div className={pageGutterClass}>
          <div className="mb-3 sm:mb-6">
            <BackButton onClick={() => navigate(-1)}>Back</BackButton>
          </div>
          <Text className="text-danger-dark">Invalid game type</Text>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={pageShellClass}>
        <div className={pageGutterClass}>
          <div className="mb-3 sm:mb-6">
            <BackButton onClick={handleBack}>Back to leaderboards</BackButton>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageShellClass}>
        <div className={pageGutterClass}>
          <div className="mb-3 sm:mb-6">
            <BackButton onClick={handleBack}>Back to leaderboards</BackButton>
          </div>
          <Text className="text-danger-dark">{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      <div className={pageGutterClass}>
        <div className="mb-3 sm:mb-6">
          <BackButton onClick={handleBack}>Back to leaderboards</BackButton>
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Heading level={1} className="mb-2">
              {gameTypeConfig.title} Leaderboard
            </Heading>
            {club && (
              <Text className="text-text-secondary">
                {club.name} • {displaySeason?.name || 'No Season Selected'}
              </Text>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <LeaderboardScopeToggle scope={scope} onScopeChange={setScope} />
            <Caption className="text-xs">
              {getScopeHelperText(scope)}
            </Caption>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <StatsCard dense title="Total Players" value={kpis.totalPlayers} variant="default" />
          <StatsCard dense title="Average ELO" value={kpis.averageElo} variant="default" />
        </div>

        <Card variant="default" size="large" className="!p-4 sm:!p-8">
          <div className="space-y-2 sm:space-y-3">
            {leaderboard.length > 0 ? (
              leaderboard.map((player, index) => {
                const isUser = player.players?.id === currentUser?.id;
                const movement = snapshotMovement.get(player.players?.id);

                return (
                  <LeaderboardRow
                    key={`player-${player.players?.id || index}`}
                    rank={player.rank}
                    player={player.players}
                    elo={player.eloRating}
                    isCurrentUser={isUser}
                    rankDelta={movement?.rank_delta ?? null}
                    eloDelta={movement?.elo_delta ?? null}
                    highlight={isUser ? 'primary' : 'none'}
                    rankTierMode="full"
                  />
                );
              })
            ) : (
              <div className="text-center py-12">
                <Text className="text-text-muted">No leaderboard data available</Text>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const FullLeaderboardPageWithSeason = ({ currentUser }) => {
  const { clubId } = useParams();

  return (
    <SeasonProvider clubId={clubId}>
      <FullLeaderboardPage currentUser={currentUser} />
    </SeasonProvider>
  );
};

export default FullLeaderboardPageWithSeason;
